// The fake api's on-account routes (F-POS-07, spec/06 §8.6), in the contract's shapes — enough for a
// player's On account section to be rendered against the fake in `fake-server.ts` at a venue:
//
//   GET  …/venues/{v}/players/{p}/credit                    getPlayerCredit      orders:read
//   PUT  …/venues/{v}/players/{p}/credit                    setPlayerCredit      credit:manage
//   POST …/venues/{v}/players/{p}/receivable/write-off      writeOffReceivable   credit:write_off
//   POST …/venues/{v}/players/{p}/receivable/settle         settleReceivable     orders:create
//
// Aarav owes ₹450.00 against a ₹2,000.00 limit; every other player owes nothing against the
// organisation's default limit. Integer paise throughout (spec/06 §1). Bodies are checked with the
// contract's own schemas; each route checks its own permission, as the server's guard does. A write
// that is more than is owed answers `409 conflict`, as `credit.ts` in the commerce module does.

import type { Problem, Receivable } from '@clavian/api-client';
import { SetPlayerCreditSchema, SettleReceivableSchema, WriteOffReceivableSchema, type z } from '@clavian/contracts';

import type { FakeResponse } from './fake-server';
import { FAKE_PLAYER_IDS } from './fake-venue-players';

const ORGANIZATION_ID = 'org_01k5znw4hmfay8vvr14d2pf2db';
const VENUE_ID = 'ven_01k5znw4hmfay8vvr14d2pf2dc';
const INR = (amount: number) => ({ amount, currency: 'INR' });
const DEFAULT_LIMIT = 100_000;

const CREDIT_ROUTE = /^\/v1\/organizations\/([^/]+)\/venues\/([^/]+)\/players\/([^/]+)\/(credit|receivable\/write-off|receivable\/settle)$/;

export interface CreditHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
  readonly gate: () => FakeResponse | undefined;
  readonly can: (permission: string) => boolean;
}

interface Account {
  owed: number;
  limit: number;
}

/** What one request did, for tests to read back. */
export interface CreditCall {
  readonly route: 'settle' | 'write-off' | 'limit';
  readonly playerId: string;
  readonly body: unknown;
}

export function createFakeCredit(helpers: CreditHelpers) {
  const { json, problem } = helpers;
  const state = {
    /** `credit.enabled` for the organisation. */
    enabled: true,
    accounts: new Map<string, Account>([[FAKE_PLAYER_IDS.aarav, { owed: 45_000, limit: 200_000 }]]),
    calls: [] as CreditCall[],
  };

  const account = (playerId: string): Account => {
    const held = state.accounts.get(playerId);
    if (held !== undefined) return held;
    const made = { owed: 0, limit: DEFAULT_LIMIT };
    state.accounts.set(playerId, made);
    return made;
  };

  const position = (playerId: string): Receivable => {
    const { owed, limit } = account(playerId);
    return { playerId, enabled: state.enabled, balance: INR(owed), limit: INR(limit), available: INR(Math.max(0, limit - owed)) };
  };

  const invalid = (error: z.ZodError) =>
    problem(400, 'validation_failed', 'Validation failed', {
      errors: error.issues.map((issue) => ({ field: issue.path.map(String).join('.') || '(request)', code: issue.code, message: issue.message })),
    });

  function handle(method: string, pathname: string, _query: URLSearchParams, input: Record<string, unknown>): FakeResponse | undefined {
    const match = CREDIT_ROUTE.exec(pathname);
    if (match === null) return undefined;
    const [, organizationId, venueId, playerId = '', tail] = match;
    if (organizationId !== ORGANIZATION_ID) return undefined;
    const refused = helpers.gate();
    if (refused !== undefined) return refused;
    if (venueId !== VENUE_ID) return problem(404, 'not_found', 'No such venue.');
    if (!(Object.values(FAKE_PLAYER_IDS) as string[]).includes(playerId)) return problem(404, 'not_found', 'No such player at this organisation.');
    const forbidden = (permission: string) => (helpers.can(permission) ? undefined : problem(403, 'forbidden', 'Not allowed'));

    switch (`${method} ${tail ?? ''}`) {
      case 'GET credit':
        return forbidden('orders:read') ?? json(200, position(playerId));
      case 'PUT credit': {
        const denied = forbidden('credit:manage');
        if (denied !== undefined) return denied;
        const parsed = SetPlayerCreditSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        state.calls.push({ route: 'limit', playerId, body: input });
        account(playerId).limit = parsed.data.limit.amount;
        return json(200, position(playerId));
      }
      case 'POST receivable/write-off': {
        const denied = forbidden('credit:write_off');
        if (denied !== undefined) return denied;
        const parsed = WriteOffReceivableSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        state.calls.push({ route: 'write-off', playerId, body: input });
        const held = account(playerId);
        if (parsed.data.amount.amount <= 0) return problem(400, 'validation_failed', 'A write-off must be more than zero.');
        if (parsed.data.amount.amount > held.owed) return problem(409, 'conflict', 'The write-off exceeds what is owed.');
        held.owed -= parsed.data.amount.amount;
        return json(200, position(playerId));
      }
      case 'POST receivable/settle': {
        const denied = forbidden('orders:create');
        if (denied !== undefined) return denied;
        const parsed = SettleReceivableSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        state.calls.push({ route: 'settle', playerId, body: input });
        const held = account(playerId);
        if (parsed.data.amount.amount <= 0) return problem(400, 'validation_failed', 'A payment must be more than zero.');
        if (parsed.data.amount.amount > held.owed) return problem(409, 'conflict', 'The payment exceeds what is owed.');
        held.owed -= parsed.data.amount.amount;
        return json(200, position(playerId));
      }
      default:
        return undefined;
    }
  }

  /**
   * Charge a sale's on-account tender to the player (F-POS-07): refused when on account is off or the
   * charge would take what they owe over their limit. Called by the commerce fake, not a route.
   */
  function charge(playerId: string, amount: number): 'ok' | 'disabled' | 'over-limit' {
    if (!state.enabled) return 'disabled';
    const held = account(playerId);
    if (held.owed + amount > held.limit) return 'over-limit';
    held.owed += amount;
    return 'ok';
  }

  return { handle, state, charge };
}

export type FakeCredit = ReturnType<typeof createFakeCredit>;
